import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, CATEGORY_LABELS } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Plus, Sparkles, MapPin, Star, Edit, Trash2, Rocket, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

export default function MerchantDashboard() {
  const { user, loading } = useAuth();
  const [places, setPlaces] = useState([]);
  const [refreshing, setRefreshing] = useState(true);

  useEffect(() => {
    if (!user) return;
    setRefreshing(true);
    api.get("/merchant/places").then(r => setPlaces(r.data)).finally(()=>setRefreshing(false));
  }, [user]);

  if (loading) return <div className="pt-32 text-center">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const promoted = places.filter(p => p.is_promoted).length;

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet établissement ?")) return;
    await api.delete(`/places/${id}`);
    setPlaces(places.filter(p => p.id !== id));
    toast.success("Supprimé");
  };

  return (
    <div className="min-h-screen bg-[#F3F0EA]" data-testid="merchant-dashboard">
      <div className="pt-24 max-w-6xl mx-auto px-6 pb-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="uppercase-label text-[#E26D5C]">Espace Commerçant</div>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter text-[#2C3627] mt-1">
              Bonjour, {user.name.split(" ")[0]} 👋
            </h1>
            <p className="text-[#5C6B55] mt-2">Gérez vos établissements et boostez leur visibilité.</p>
          </div>
          <Link to="/merchant/new" data-testid="add-place-btn">
            <Button className="rounded-full bg-[#E26D5C] hover:bg-[#D15B4A] text-white font-semibold py-6 px-6 shadow-md shadow-[#E26D5C]/20">
              <Plus size={16} className="mr-2"/> Ajouter un lieu
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard label="Établissements" value={places.length} testid="stat-total"/>
          <StatCard label="En promotion" value={promoted} accent testid="stat-promoted"/>
          <StatCard label="Note moyenne" value={places.length ? (places.reduce((a,p)=>a+(p.rating||0),0)/places.length).toFixed(1) : "—"} testid="stat-avg"/>
        </div>

        {refreshing && <div className="text-center text-[#5C6B55] py-8">Chargement…</div>}

        {!refreshing && places.length === 0 && (
          <div className="rounded-3xl bg-white border border-black/5 p-12 text-center shadow-sm">
            <div className="w-16 h-16 rounded-2xl bg-[#FFF9F2] mx-auto flex items-center justify-center mb-4">
              <MapPin size={28} className="text-[#E26D5C]"/>
            </div>
            <h3 className="font-display font-bold text-xl">Aucun établissement</h3>
            <p className="text-[#5C6B55] mt-1">Ajoutez votre premier lieu pour le faire découvrir.</p>
            <Link to="/merchant/new" data-testid="empty-add-btn"><Button className="mt-4 rounded-full bg-[#E26D5C] hover:bg-[#D15B4A]">Ajouter un lieu</Button></Link>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {places.map(p => (
            <div key={p.id} data-testid={`merchant-place-${p.id}`} className="bg-white rounded-3xl overflow-hidden border border-black/5 shadow-sm">
              <div className="relative h-40 bg-[#F3F0EA]">
                {p.image_url && <img src={p.image_url} className="w-full h-full object-cover" alt={p.name}/>}
                {p.is_promoted && (
                  <div className="absolute top-3 right-3 bg-gradient-to-r from-[#F4A261] to-[#E26D5C] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full flex items-center gap-1">
                    <BadgeCheck size={10}/> Boosté
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="uppercase-label text-[#5C6B55]">{CATEGORY_LABELS[p.category]}</div>
                <h3 className="font-display font-bold text-lg mt-1">{p.name}</h3>
                <div className="flex items-center gap-1 text-xs text-[#5C6B55] mt-1">
                  <Star size={12} className="fill-[#F4A261] text-[#F4A261]"/>
                  {p.rating?.toFixed(1)} · {p.city}
                </div>
                <div className="flex gap-2 mt-4">
                  <Link to={`/merchant/promote/${p.id}`} className="flex-1" data-testid={`promote-btn-${p.id}`}>
                    <Button className="w-full rounded-full bg-[#2C3627] hover:bg-[#1F2A1A] text-white text-xs">
                      <Rocket size={12} className="mr-1"/> Booster
                    </Button>
                  </Link>
                  <Link to={`/merchant/edit/${p.id}`} data-testid={`edit-btn-${p.id}`}>
                    <Button variant="outline" size="icon" className="rounded-full"><Edit size={14}/></Button>
                  </Link>
                  <Button variant="outline" size="icon" className="rounded-full text-red-600" onClick={()=>remove(p.id)} data-testid={`delete-btn-${p.id}`}>
                    <Trash2 size={14}/>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className={`rounded-3xl p-6 border ${accent ? "bg-[#FFF9F2] border-[#F4A261]/40" : "bg-white border-black/5"}`}>
      <div className="uppercase-label text-[#5C6B55]">{label}</div>
      <div className="font-display font-black text-4xl text-[#2C3627] mt-2">{value}</div>
      {accent && <div className="text-xs text-[#E26D5C] font-semibold mt-1 flex items-center gap-1"><Sparkles size={12}/> Mise en avant active</div>}
    </div>
  );
}
